import React from "react";

type CollectionSkeletonProps = {
  /**
   * `page`: guest/setup first paint (sidebar ghost + header + table).
   * `inset`: rows + toolbar inside the collection's existing chrome.
   */
  variant?: "page" | "inset";
};

const Pulse: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`motion-safe:animate-pulse rounded-md bg-od-surface-2 ${className}`} />
);

const ToolbarAndRows: React.FC = () => (
  <>
    <div className="flex flex-wrap items-center gap-2 border-b border-od-border bg-od-surface px-2.5 py-2">
      <Pulse className="h-8 min-w-0 basis-40 flex-1 sm:max-w-xs" />
      <div className="ml-auto flex items-center gap-1.5">
        <Pulse className="h-8 w-8" />
        <Pulse className="h-8 w-8" />
        <Pulse className="h-8 w-16" />
      </div>
    </div>
    <div className="flex min-h-0 flex-1 flex-col">
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-od-border px-3 py-2.5"
        >
          <Pulse className="h-4 w-8 shrink-0" />
          <Pulse className="h-4 flex-1" />
          <Pulse className="h-4 w-24 shrink-0 hidden sm:block" />
          <Pulse className="h-4 w-16 shrink-0 hidden md:block" />
        </div>
      ))}
    </div>
  </>
);

const statusCopy = (
  <span className="sr-only">Loading your dashboard…</span>
);

export const CollectionSkeleton: React.FC<CollectionSkeletonProps> = ({
  variant = "page",
}) => {
  if (variant === "inset") {
    return (
      <div
        role="status"
        aria-busy="true"
        className="flex min-h-0 flex-1 flex-col"
      >
        {statusCopy}
        <ToolbarAndRows />
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-busy="true"
      className="flex min-h-screen bg-od-bg"
    >
      {statusCopy}

      <aside className="hidden md:flex w-64 lg:w-72 shrink-0 flex-col border-r border-od-border bg-od-surface">
        <div className="flex items-center p-2.5 border-b border-od-border">
          <Pulse className="h-7 w-12" />
        </div>
        <div className="border-b border-od-border px-3 py-2.5">
          <Pulse className="h-5 w-16 rounded-full" />
        </div>
        <div className="flex flex-col gap-2 p-3">
          <Pulse className="h-8 w-full" />
          <Pulse className="h-6 w-3/4" />
          <Pulse className="h-6 w-2/3" />
          <Pulse className="h-6 w-3/4" />
          <Pulse className="h-6 w-1/2" />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="md:hidden sticky top-0 z-40 flex items-center gap-3 px-4 bg-od-surface border-b border-od-border"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            paddingLeft: "max(1rem, env(safe-area-inset-left))",
            paddingRight: "max(1rem, env(safe-area-inset-right))",
            minHeight: "calc(3.5rem + env(safe-area-inset-top))",
          }}
        >
          <Pulse className="h-8 w-8 shrink-0" />
          <Pulse className="h-4 w-24" />
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3 md:p-4">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <Pulse className="h-6 w-40" />
              <Pulse className="h-4 w-16" />
            </div>
            <Pulse className="h-9 w-28 rounded-md" />
          </header>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-od-border bg-od-surface">
            <ToolbarAndRows />
          </div>
        </div>
      </div>
    </div>
  );
};
